/**
 * DHT Sensors Panel Component
 */

import { serial } from '../serial/serial.js';
import { notifications } from './notifications.js';
import { t } from '../utils/i18n.js';

export class DHTPanel {
    constructor(pinsPanel) {
        this._pinsPanel = pinsPanel;
        this._container = document.getElementById('dht-table-body');
        this._autoUpdateInterval = null;
        this._readInProgress = false;
        this._readGeneration = 0;
        this._data = {};

        this._render();
        this._bindEvents();
    }

    _render() {
        let html = '';
        for (let pin = 1; pin <= 10; pin++) {
            html += `
                <tr data-pin="${pin}">
                    <td><span class="pin-number">${pin}</span></td>
                    <td>
                        <span class="dht-status">
                            <span class="status-dot" data-pin="${pin}"></span>
                            <span class="status-text" data-pin="${pin}">-</span>
                        </span>
                    </td>
                    <td><span class="temp-value" data-pin="${pin}">-</span></td>
                    <td><span class="humidity-value" data-pin="${pin}">-</span></td>
                    <td><span class="last-update" data-pin="${pin}">-</span></td>
                </tr>
            `;
        }
        this._container.innerHTML = html;
    }

    _bindEvents() {
        // Read All button
        document.getElementById('btn-dht-read-all')?.addEventListener('click', () => {
            this.readAllDHT();
        });

        // Auto update checkbox
        document.getElementById('dht-auto-update')?.addEventListener('change', (e) => {
            if (e.target.checked) {
                this._startAutoUpdate();
            } else {
                this._stopAutoUpdate();
            }
        });

        // Interval change
        document.getElementById('dht-interval')?.addEventListener('change', (e) => {
            if (this._autoUpdateInterval) {
                this._stopAutoUpdate();
                this._startAutoUpdate();
            }
        });
    }

    _startAutoUpdate() {
        if (this._autoUpdateInterval) return;
        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            this.stop();
            return;
        }

        const interval = parseInt(document.getElementById('dht-interval')?.value || 2000);
        this._autoUpdateInterval = setInterval(() => {
            this.readAllDHT();
        }, interval);
        this.readAllDHT();
    }

    _stopAutoUpdate() {
        // Invalidate a read that may currently be awaiting a device response.
        this._readGeneration++;
        if (this._autoUpdateInterval) {
            clearInterval(this._autoUpdateInterval);
            this._autoUpdateInterval = null;
        }
    }

    async readAllDHT() {
        if (this._readInProgress) {
            return;
        }

        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        this._readInProgress = true;
        const generation = this._readGeneration;
        const modes = this._pinsPanel.modes;

        try {
            for (let pin = 1; pin <= 10; pin++) {
                if (!serial.connected || generation !== this._readGeneration) {
                    break;
                }

                const mode = modes[pin - 1];
                const statusDot = this._container.querySelector(`.status-dot[data-pin="${pin}"]`);
                const statusText = this._container.querySelector(`.status-text[data-pin="${pin}"]`);
                const tempEl = this._container.querySelector(`.temp-value[data-pin="${pin}"]`);
                const humidityEl = this._container.querySelector(`.humidity-value[data-pin="${pin}"]`);
                const updateEl = this._container.querySelector(`.last-update[data-pin="${pin}"]`);

                if (mode !== 'D') {
                    statusDot.className = 'status-dot';
                    statusText.textContent = t('not-dht');
                    tempEl.textContent = '-';
                    humidityEl.textContent = '-';
                    continue;
                }

                try {
                    const value = await serial.readPin(pin, 3000);
                    if (!serial.connected || generation !== this._readGeneration) {
                        break;
                    }
                    const parsed = this._parseDHTValue(value);

                    if (parsed.ok) {
                        this._data[pin] = {
                            temp: parsed.temp,
                            humidity: parsed.humidity,
                            timestamp: Date.now()
                        };

                        statusDot.className = 'status-dot ok';
                        statusText.textContent = t('ok');
                        tempEl.textContent = `${parsed.temp.toFixed(1)}°C`;
                        humidityEl.textContent = `${parsed.humidity.toFixed(1)}%`;
                        updateEl.textContent = new Date().toLocaleTimeString();
                    } else {
                        statusDot.className = 'status-dot error';
                        statusText.textContent = parsed.status;
                        tempEl.textContent = parsed.value || '-';
                        humidityEl.textContent = '-';
                    }
                } catch (error) {
                    if (!serial.connected || generation !== this._readGeneration) {
                        break;
                    }
                    statusDot.className = 'status-dot error';
                    statusText.textContent = error.message || t('error');
                    tempEl.textContent = '-';
                    humidityEl.textContent = '-';
                }

                // Small delay between reads
                await new Promise(r => setTimeout(r, 100));
            }
        } finally {
            this._readInProgress = false;
        }
    }

    _parseDHTValue(value) {
        const data = String(value ?? '').trim();
        if (!data) {
            return { ok: false, status: t('no-data') };
        }

        const upper = data.toUpperCase();
        const errorMessages = ['ERROR', 'TIMEOUT', 'CHECKSUM', 'NO RESPONSE', 'FAIL'];
        if (errorMessages.some(error => upper.includes(error))) {
            return { ok: false, status: `${t('error')}: ${data}` };
        }

        // Accept both Latin C and Cyrillic С in formats like 25.3C,60.2%.
        const match = data.match(/(-?\d+(?:\.\d+)?)\s*[CcСс]\s*,\s*(-?\d+(?:\.\d+)?)\s*%/);
        if (!match) {
            return { ok: false, status: t('error'), value: data };
        }

        const temp = parseFloat(match[1]);
        const humidity = parseFloat(match[2]);
        if (Number.isNaN(temp) || Number.isNaN(humidity)) {
            return { ok: false, status: t('invalid-value'), value: data };
        }

        return { ok: true, temp, humidity };
    }

    get data() {
        return this._data;
    }

    stop() {
        const checkbox = document.getElementById('dht-auto-update');
        if (checkbox) checkbox.checked = false;
        this._stopAutoUpdate();
    }

    destroy() {
        this.stop();
    }
}
